import React, { useCallback, useEffect, useState } from "react";
import { Moon, Sprout, Warehouse, ClipboardCheck, CheckCircle2, Clock, Printer, Download, MessageCircle, ArrowRight, PackageCheck, Layers, Inbox } from "lucide-react";
import api from "../lib/api";
import { Card, Badge, Button, PageHeader, PageLoader } from "../lib/ui";
import { fmtRp, fmtNum, fmtWIB, fmtDateWIB, orderOwner, ORDER_STATUS } from "../lib/constants";
import { useToast } from "../lib/toast";
import { useT } from "../lib/i18n";
import OpsProgress from "../components/OpsProgress";
import OrderDetailModal from "../components/OrderDetailModal";
import { usePrintDoc } from "../lib/usePrintDoc";

function Step({ n, title, icon: Icon, done, children, testid }) {
  return (
    <Card className="overflow-hidden p-0" data-testid={testid}>
      <div className="flex items-center gap-3 border-b border-line px-5 py-3.5">
        <span className={`flex h-8 w-8 items-center justify-center rounded-xl text-sm font-extrabold ${done ? "bg-emerald-500 text-white" : "bg-primary-light text-primary"}`}>
          {done ? <CheckCircle2 size={18} /> : n}
        </span>
        <Icon size={18} className="text-ink-soft" />
        <h3 className="font-heading text-base font-extrabold text-ink">{title}</h3>
      </div>
      <div className="p-5">{children}</div>
    </Card>
  );
}

export default function CentralNight() {
  const { t } = useT();
  const toast = useToast();
  const { printDoc, exportDoc, printing } = usePrintDoc();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [viewId, setViewId] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/workspace/central-night"); setData(data); } catch (e) { /* */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const advance = async (action) => {
    setBusy(action);
    try {
      const { data: r } = await api.post("/workspace/central/advance", { action });
      toast.success(`${r.updated} ${t("orders updated")}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
    finally { setBusy(null); }
  };

  const check = async (it, branch) => {
    try {
      await api.post("/workspace/physical-check", {
        context: "warehouse_pick", scope_key: it.scope_key, label: `${branch.branch_name} · ${it.name}`,
        branch_id: branch.branch_id, product_id: it.product_id, checked: !it.checked });
      load();
    } catch (e) { toast.error(t("Failed")); }
  };

  const printFresh = () => printDoc("/workspace/fresh-shopping-list/pdf", { key: "fresh-print", label: t("Fresh Market Shopping List") });
  const exportFresh = () => exportDoc("/workspace/fresh-shopping-list/pdf", { key: "fresh-export", label: t("Fresh Market Shopping List") });

  if (!data) return <PageLoader />;
  const s = data.summary;
  const m = data.milestones;

  return (
    <div className="animate-fade-up space-y-5 pb-24 lg:pb-2" data-testid="central-night">
      <PageHeader icon={Moon} title={t("Central Operations Night")} subtitle={t("Planning — consolidate branch orders & fresh market")} />
      <OpsProgress items={data.progress.map((p) => (p.key === "consolidation" ? { ...p, label: "Branch Order Summary" } : p))} testid="cn-progress" />

      {/* Step 1 — Incoming Orders Inbox */}
      <Step n={1} title={t("Incoming Orders Inbox")} icon={Inbox} done={s.branches_submitted === s.branches_total} testid="cn-step1">
        <p className="mb-3 text-xs text-ink-mute">{t("Live inbox of every branch order — who submitted, who is late, who is still editing.")}</p>
        {/* Branch coverage chips */}
        <div className="mb-4 flex flex-wrap gap-2" data-testid="cn-coverage">
          {data.branches.map((b) => (
            <span key={b.branch_id} data-testid={`cn-branch-${b.branch_id}`}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${b.late ? "border-danger bg-danger-light text-danger-text" : b.submitted ? "border-emerald-200 bg-emerald-50 text-emerald-700" : b.editing ? "border-amber-200 bg-amber-50 text-amber-700" : "border-line bg-app text-ink-mute"}`}>
              {b.submitted ? <CheckCircle2 size={13} /> : <Clock size={13} />}
              {b.name} · {b.submitted ? t("Submitted") : b.late ? t("Late") : b.editing ? t("Editing") : t("Not submitted")}
            </span>
          ))}
        </div>
        {/* Order inbox */}
        {(!data.inbox || data.inbox.length === 0) ? (
          <p className="py-6 text-center text-sm text-ink-mute">{t("No orders received yet")}</p>
        ) : (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {data.inbox.map((o) => {
              const owner = orderOwner(o);
              const st = ORDER_STATUS[o.status] || { label: o.status, tone: "mute" };
              return (
                <div key={o.order_id} onClick={() => setViewId(o.order_id)} data-testid={`cn-inbox-${o.order_no}`}
                  className={`cursor-pointer rounded-xl border p-3 transition hover:shadow-card ${o.late ? "border-danger/40 bg-danger-light/40" : "border-line bg-white hover:bg-app"}`}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="min-w-0 truncate font-heading text-base font-extrabold text-ink">{o.branch_name}</p>
                    <div className="flex shrink-0 items-center gap-1">
                      {o.late && <Badge tone="danger"><Clock size={11} /> {t("Late")}</Badge>}
                      <Badge tone={st.tone}>{t(st.label)}</Badge>
                    </div>
                  </div>
                  <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                    <div><p className="font-semibold uppercase text-ink-mute">{t("Submitted")}</p><p className="font-semibold text-ink">{o.submitted_at ? fmtWIB(o.submitted_at) : <span className="text-ink-mute">{o.status === "DRAFT" ? t("Editing") : t("Not submitted")}</span>}</p></div>
                    <div><p className="font-semibold uppercase text-ink-mute">{t("Delivery")}</p><p className="font-semibold text-ink">{fmtDateWIB(o.requested_delivery_date || o.order_date)}</p></div>
                  </div>
                  <div className="mt-2 flex items-center justify-between border-t border-line pt-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-ink">{o.item_count} {t("items")}</span>
                      {owner && <Badge tone={owner.tone}>{t(owner.label)}</Badge>}
                    </div>
                    <span className="font-mono text-[10px] text-ink-mute">Ref: {o.order_no}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Step>

      {/* Step 2 — Consolidation */}
      <Step n={2} title={t("Branch Order Summary")} icon={Layers} done={s.orders_submitted === 0 && (s.orders_approved + s.orders_purchased) > 0} testid="cn-step2">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <div className="rounded-xl bg-emerald-50 p-3"><p className="text-xs font-bold text-emerald-700">{t("Fresh Market")}</p><p className="font-heading text-xl font-extrabold text-ink">{s.fresh_count}</p><p className="text-[11px] text-ink-mute">~{fmtRp(s.fresh_value)}</p></div>
          <div className="rounded-xl bg-blue-50 p-3"><p className="text-xs font-bold text-blue-700">{t("Central Warehouse")}</p><p className="font-heading text-xl font-extrabold text-ink">{s.warehouse_count}</p><p className="text-[11px] text-ink-mute">~{fmtRp(s.warehouse_value)}</p></div>
          <div className="rounded-xl bg-app p-3"><p className="text-xs font-bold text-ink-mute">{t("Submitted")}</p><p className="font-heading text-xl font-extrabold text-ink">{s.orders_submitted}</p></div>
          <div className="rounded-xl bg-app p-3"><p className="text-xs font-bold text-ink-mute">{t("Approved")}</p><p className="font-heading text-xl font-extrabold text-ink">{s.orders_approved}</p></div>
        </div>
        <Button className="mt-4 w-full" disabled={s.orders_submitted === 0} loading={busy === "approve"} onClick={() => advance("approve")} data-testid="cn-approve">
          <ClipboardCheck size={16} /> {t("Approve Branch Orders")} ({s.orders_submitted})
        </Button>
      </Step>

      {/* Step 3 — Fresh Market Shopping List (Product × Branch matrix) */}
      <Step n={3} title={t("Fresh Market Shopping List")} icon={Sprout} done={m.fresh_ordered} testid="cn-step3">
        <p className="mb-3 text-xs text-ink-mute">{t("Total quantity per product across all branches — the purchasing team's market shopping list.")}</p>
        {(!data.fresh_matrix || data.fresh_matrix.rows.length === 0) ? (
          <p className="py-6 text-center text-sm text-ink-mute">{t("No fresh market items today")}</p>
        ) : (
          <div className="space-y-3">
            <div className="overflow-x-auto rounded-xl border border-line" data-testid="cn-fresh-matrix">
              <table className="w-full min-w-[520px] text-sm">
                <thead>
                  <tr className="border-b border-line bg-emerald-50 text-left">
                    <th className="px-3 py-2 font-bold text-emerald-800">{t("Product")}</th>
                    {data.fresh_matrix.branches.map((b) => (
                      <th key={b.branch_id} className="whitespace-nowrap px-3 py-2 text-right font-bold text-emerald-800">{b.branch_name}</th>
                    ))}
                    <th className="whitespace-nowrap px-3 py-2 text-right font-extrabold text-emerald-900">{t("Total to Buy")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {data.fresh_matrix.rows.map((r) => (
                    <tr key={r.product_id} className="hover:bg-emerald-50/40" data-testid={`cn-fresh-row-${r.product_id}`}>
                      <td className="px-3 py-2 font-medium text-ink">{r.name}</td>
                      {data.fresh_matrix.branches.map((b) => (
                        <td key={b.branch_id} className="whitespace-nowrap px-3 py-2 text-right text-ink-soft">
                          {r.cells[b.branch_id] ? <>{fmtNum(r.cells[b.branch_id])} <span className="text-[11px] text-ink-mute">{r.unit}</span></> : <span className="text-ink-mute">—</span>}
                        </td>
                      ))}
                      <td className="whitespace-nowrap bg-amber-50/60 px-3 py-2 text-right font-heading font-extrabold text-ink">{fmtNum(r.total)} <span className="text-[11px] font-medium text-ink-mute">{r.unit}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data.fresh_matrix.totals_by_unit?.length > 0 && (
              <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50/60 px-4 py-3" data-testid="cn-fresh-totals">
                <p className="mb-1.5 text-xs font-bold uppercase tracking-wide text-amber-700">{t("Grand Total")}</p>
                <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
                  {data.fresh_matrix.totals_by_unit.map((u) => (
                    <span key={u.unit} className="text-ink"><b>{fmtNum(u.qty)}</b> {String(u.unit).toLowerCase()}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        <div className="mt-4 flex flex-wrap gap-2">
          <Button className="flex-1" disabled={m.fresh_ordered || s.orders_approved === 0} loading={busy === "purchase"} onClick={() => advance("purchase")} data-testid="cn-mark-ordered">
            <PackageCheck size={16} /> {m.fresh_ordered ? t("Marked as Ordered") : t("Mark as Ordered")}
          </Button>
          <Button variant="outline" loading={printing === "fresh-print"} onClick={printFresh} data-testid="cn-print-fresh"><Printer size={16} /> {t("Print")}</Button>
          <Button variant="outline" loading={printing === "fresh-export"} onClick={exportFresh} data-testid="cn-export-fresh"><Download size={16} /> {t("Export PDF")}</Button>
          <Button variant="outline" disabled title={t("Coming Soon")} data-testid="cn-whatsapp"><MessageCircle size={16} /> WhatsApp · {t("Coming Soon")}</Button>
        </div>
      </Step>

      {/* Step 4 — Warehouse Preparation */}
      <Step n={4} title={t("Warehouse Preparation")} icon={Warehouse} done={m.warehouse_prepared} testid="cn-step4">
        <p className="mb-3 text-xs text-ink-mute">{t("Check each item while preparing tomorrow's delivery — recorded for the audit trail.")}</p>
        <div className="space-y-3">
          {data.picklist.length === 0 ? <p className="py-4 text-center text-sm text-ink-mute">{t("No demand in this queue today")}</p> :
            data.picklist.map((g) => (
              <div key={g.branch_id} className="rounded-xl border border-line" data-testid={`cn-pick-${g.branch_id}`}>
                <div className="flex items-center gap-2 border-b border-line bg-app px-4 py-2">
                  <p className="font-bold text-ink">{g.branch_name}</p>
                  <Badge tone={g.complete ? "success" : "mute"} className="ml-auto">{g.checked}/{g.total}</Badge>
                  <Button size="sm" variant="outline" loading={printing === `pick-print-${g.branch_id}`}
                    onClick={() => printDoc("/workspace/branch-picklist/pdf", { key: `pick-print-${g.branch_id}`, label: `${g.branch_name} · ${t("Picking List")}`, params: { branch_id: g.branch_id } })}
                    data-testid={`cn-print-pick-${g.branch_id}`}><Printer size={14} /> {t("Print")}</Button>
                  <Button size="sm" variant="outline" loading={printing === `pick-export-${g.branch_id}`}
                    onClick={() => exportDoc("/workspace/branch-picklist/pdf", { key: `pick-export-${g.branch_id}`, label: `${g.branch_name} · ${t("Picking List")}`, params: { branch_id: g.branch_id } })}
                    data-testid={`cn-export-pick-${g.branch_id}`}><Download size={14} /> {t("Export PDF")}</Button>
                </div>
                <div className="divide-y divide-line">
                  {g.items.map((it) => (
                    <label key={it.scope_key} className="flex cursor-pointer items-center gap-3 px-4 py-2.5 hover:bg-app" data-testid={`cn-pickitem-${it.scope_key}`}>
                      <input type="checkbox" checked={it.checked} onChange={() => check(it, g)} className="h-4 w-4 rounded border-line text-primary focus:ring-primary" data-testid={`cn-check-${it.scope_key}`} />
                      <span className={`min-w-0 flex-1 truncate text-sm ${it.checked ? "font-semibold text-ink line-through opacity-60" : "font-medium text-ink"}`}>{it.name}</span>
                      <span className="text-sm font-bold text-ink-soft">{it.qty} {it.unit}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
        </div>
        <Button className="mt-4 w-full" disabled={m.warehouse_prepared || s.orders_purchased === 0} loading={busy === "pick"} onClick={() => advance("pick")} data-testid="cn-mark-prepared">
          <ClipboardCheck size={16} /> {m.warehouse_prepared ? t("Picking Prepared") : t("Mark Picking Prepared")}
        </Button>
      </Step>

      {/* Step 5 — Complete */}
      <Card className={`flex items-center gap-4 p-5 ${data.all_done ? "border-emerald-200 bg-emerald-50" : ""}`} data-testid="cn-step5">
        <span className={`flex h-12 w-12 items-center justify-center rounded-2xl ${data.all_done ? "bg-emerald-500 text-white" : "bg-primary-light text-primary"}`}>
          {data.all_done ? <CheckCircle2 size={24} /> : <Moon size={24} />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-heading text-base font-extrabold text-ink">{data.all_done ? t("Tomorrow is Ready") : t("Central Night Complete")}</p>
          <p className="text-xs text-ink-mute">{t("Hands over to")} <b className="text-ink">{t("Central Morning Operations")}</b></p>
        </div>
        <ArrowRight size={18} className="text-primary" />
      </Card>

      <OrderDetailModal orderId={viewId} onClose={() => setViewId(null)} />
    </div>
  );
}
